/* eslint-disable react/jsx-props-no-spreading */
// @ts-check
import React from 'react';
import ReactSelect, { components } from 'react-select';
import { createGlobalStyle } from 'styled-components';

import { dropdownStyles } from './dropdown-config';

const GlobalStyle = createGlobalStyle`
  .menu {
    animation: fadeIn 0.2s ease-in-out;
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: translateY(2rem);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/**
 * @typedef {import('react-select').Props & { portalMenu?: boolean }} DropdownProps
 */

/**
 * @param {DropdownProps} props
 */
const Dropdown = ({ onChange, portalMenu = false, ...props }) => {
  const defaultMenuPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined;

  const propStyles = props.styles || {};
  const dropdownMenuPortal = dropdownStyles && dropdownStyles.menuPortal;
  const propMenuPortal = propStyles && propStyles.menuPortal;

  // Auto-enable portal behavior if menuPortalTarget is explicitly provided
  const isPortalEnabled = portalMenu || props.menuPortalTarget !== undefined;

  // Z-index stacking order: Bootstrap fixed-top (1030) < Modal (2000) < Portal Dropdowns (3000)
  // Portal dropdowns need higher z-index than modals to prevent clipping when rendered inside modals
  const mergedStyles = {
    ...dropdownStyles,
    ...propStyles,
    // Only apply menuPortal styles when using portal rendering
    ...(isPortalEnabled
      ? {
          menuPortal: (base, state) => {
            /** @type {any} */
            let next = base;
            if (typeof dropdownMenuPortal === 'function') {
              next = dropdownMenuPortal(next, state);
            }
            if (typeof propMenuPortal === 'function') {
              next = propMenuPortal(next, state);
            }

            const rawZ = next && next.zIndex;
            const parsedZ =
              typeof rawZ === 'number'
                ? rawZ
                : typeof rawZ === 'string'
                ? parseInt(rawZ, 10)
                : NaN;
            const currentZ = Number.isFinite(parsedZ) ? parsedZ : 0;

            return {
              ...next,
              zIndex: Math.max(currentZ, 3000),
            };
          },
        }
      : {}),
  };

  return (
    <>
      <GlobalStyle />
      <ReactSelect
        {...props}
        {...(isPortalEnabled
          ? {
              menuPortalTarget:
                props.menuPortalTarget ?? defaultMenuPortalTarget,
              menuPosition: props.menuPosition ?? 'fixed',
            }
          : {})}
        components={{
          Menu: (props) => <components.Menu {...props} className="menu" />,
          ...props.components,
        }}
        styles={mergedStyles}
        onChange={onChange}
      />
    </>
  );
};

export { Dropdown };
